// JSON-LD builders and the head-tag list shared by <Seo /> (runtime) and
// scripts/prerender.mjs (build time), so both emit identical tags.

import { SITE_URL, ORG, DEFAULT_OG_IMAGE } from "./site.js";
import { buildEventPath } from "../utils/eventSlug.js";

export const absoluteUrl = (path = "/") =>
  /^https?:\/\//i.test(path) ? path : `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;

const ORG_ID = `${SITE_URL}/#organization`;

const postalAddress = () => ({
  "@type": "PostalAddress",
  streetAddress: ORG.venue.streetAddress,
  addressLocality: ORG.venue.locality,
  addressRegion: ORG.venue.region,
  postalCode: ORG.venue.postcode,
  addressCountry: ORG.venue.country,
});

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  "@id": ORG_ID,
  name: ORG.name,
  alternateName: ORG.shortName,
  url: `${SITE_URL}/`,
  logo: ORG.logo,
  description: ORG.description,
  email: ORG.email,
  telephone: ORG.phones.committee.tel,
  address: postalAddress(),
  location: {
    "@type": "Place",
    name: ORG.venue.name,
    address: postalAddress(),
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "enquiries",
      telephone: ORG.phones.committee.tel,
      email: ORG.email,
      areaServed: "AU",
      availableLanguage: ["English", "Tamil"],
    },
    {
      "@type": "ContactPoint",
      contactType: "principal",
      telephone: ORG.phones.principal.tel,
      areaServed: "AU",
      availableLanguage: ["English", "Tamil"],
    },
  ],
  sameAs: ORG.sameAs,
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: ORG.name,
  alternateName: ORG.shortName,
  url: `${SITE_URL}/`,
  inLanguage: "en-AU",
  publisher: { "@id": ORG_ID },
});

// crumbs: [{ name, path? }] - the last crumb is the current page.
export const breadcrumbSchema = (crumbs, currentPath) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [{ name: "Home", path: "/" }, ...crumbs].map((crumb, index, all) => ({
    "@type": "ListItem",
    position: index + 1,
    name: crumb.name,
    item: absoluteUrl(index === all.length - 1 ? currentPath : crumb.path),
  })),
});

// The API stores event dates and times as UTC strings whose digits are the
// Sydney wall-clock values (see formatEventDate in EventDetails.jsx). Rebuild
// that local time and attach the real Sydney offset for that day (AEST/AEDT).
const sydneyOffset = (isoDate) => {
  try {
    const name = new Intl.DateTimeFormat("en-AU", {
      timeZone: "Australia/Sydney",
      timeZoneName: "longOffset",
    })
      .formatToParts(new Date(`${isoDate}T12:00:00Z`))
      .find((part) => part.type === "timeZoneName")?.value;
    const match = name?.match(/GMT([+-]\d{2}:\d{2})/);
    return match ? match[1] : "+10:00";
  } catch {
    return "+10:00";
  }
};

const eventDateTime = (dateString, timeString) => {
  if (!dateString) return null;
  const date = String(dateString).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const time = timeString ? String(timeString).slice(11, 19) : "";
  if (!/^\d{2}:\d{2}:\d{2}$/.test(time)) return date;
  return `${date}T${time}${sydneyOffset(date)}`;
};

export const stripHtml = (html = "") =>
  String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const eventImage = (event) =>
  event?.assets?.find((asset) => asset.asset_type === "img")?.asset_value || null;

// Only emit Event markup when the event has a real start date and is held at
// our confirmed venue - Google requires a location address, and we only know
// the address of Walters Road Public School.
export const eventSchema = (event, path) => {
  const venue = event?.event_venue;
  const startDate = eventDateTime(venue?.start_date, venue?.start_time);
  if (!startDate || !/walters\s*r(oa)?d/i.test(venue?.venue || "")) return null;

  const endDate = eventDateTime(venue?.end_date || venue?.start_date, venue?.end_time);
  const image = eventImage(event);
  const description = stripHtml(event.article_shortdesc || event.article_longdesc).slice(0, 300);

  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: event.article_title,
    ...(description && { description }),
    startDate,
    ...(endDate && { endDate }),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: venue.venue,
      address: postalAddress(),
    },
    ...(image && { image: [image] }),
    organizer: { "@type": "Organization", "@id": ORG_ID, name: ORG.name, url: `${SITE_URL}/` },
    url: absoluteUrl(path),
  };
};

// Page entry (same shape as src/seo/pages.js) for one event from the API.
// Used by EventDetails at runtime and by the prerender script at build time.
export const eventPage = (event) => {
  const path = `${buildEventPath(event)}/`;
  const image = eventImage(event);
  const schema = eventSchema(event, path);
  return {
    path,
    title: `${event.article_title} | WSTSC Events`,
    description:
      stripHtml(event.article_shortdesc || event.article_longdesc).slice(0, 160) ||
      event.article_title,
    ogType: "article",
    ...(image && { image: { url: image, alt: event.article_title } }),
    breadcrumbs: [{ name: "Events", path: "/events/" }, { name: event.article_title }],
    jsonLd: schema ? [schema] : [],
  };
};

export const pageSchemas = (page) => {
  const schemas = [];
  if (page.schema?.includes("organization")) schemas.push(organizationSchema());
  if (page.schema?.includes("website")) schemas.push(websiteSchema());
  if (page.breadcrumbs?.length && !page.noindex) {
    schemas.push(breadcrumbSchema(page.breadcrumbs, page.path));
  }
  return schemas;
};

// Returns { title, tags: [[tagName, attrs]], jsonLd: [objects] } for a page.
// `indexable` is false on staging builds so nothing there gets indexed.
export const buildHead = (page, { indexable = true } = {}) => {
  const noindex = page.noindex || !indexable;
  const url = absoluteUrl(page.path);
  const image = page.image || DEFAULT_OG_IMAGE;

  const tags = [
    ["meta", { name: "description", content: page.description }],
    [
      "meta",
      {
        name: "robots",
        content: noindex ? "noindex, follow" : "index, follow, max-image-preview:large",
      },
    ],
    ...(noindex ? [] : [["link", { rel: "canonical", href: url }]]),
    ["meta", { property: "og:site_name", content: ORG.name }],
    ["meta", { property: "og:locale", content: "en_AU" }],
    ["meta", { property: "og:type", content: page.ogType || "website" }],
    ["meta", { property: "og:title", content: page.ogTitle || page.title }],
    ["meta", { property: "og:description", content: page.description }],
    ["meta", { property: "og:url", content: url }],
    ["meta", { property: "og:image", content: image.url }],
    ...(image.width ? [["meta", { property: "og:image:width", content: String(image.width) }]] : []),
    ...(image.height ? [["meta", { property: "og:image:height", content: String(image.height) }]] : []),
    ["meta", { property: "og:image:alt", content: image.alt }],
    ["meta", { name: "twitter:card", content: "summary_large_image" }],
    ["meta", { name: "twitter:title", content: page.ogTitle || page.title }],
    ["meta", { name: "twitter:description", content: page.description }],
    ["meta", { name: "twitter:image", content: image.url }],
    ["meta", { name: "twitter:image:alt", content: image.alt }],
  ];

  const jsonLd = noindex ? [] : [...pageSchemas(page), ...(page.jsonLd || [])];

  return { title: page.title, tags, jsonLd };
};
